import React, { useState, useRef, MutableRefObject } from 'react';
import NextImage from 'next/image';
import { useTheme } from 'styled-components';
import { useSpring, animated } from '@react-spring/web';

import useBreakpoint from 'Common/hooks/useBreakpoint';

import { H5 } from 'Elements/Typography';

import { VideoProps } from 'Components/Video';
import Grid, { Col } from 'Components/Grid';

import { Wave, WaveGrey } from 'Modules/HeroBanner/MakeWaves/MakeWaves.styled';

import Inner, { WaveHeroInnerProps } from '../subcomponents/Inner';
import GraduateStoriesInner, {
    GraduateStoriesInnerProps
} from '../subcomponents/GraduateStoriesInner';

import {
    Container,
    VideoWrapper,
    VideoInner,
    CloseButton,
    CloseIcon,
    VideoDescription,
    ImageWrapper,
    PlayButton,
    PlayIcon,
    StyledTranscriptLink,
    Iframe,
    ResponsiveVideoWrapper
} from './Video.styled';

export interface Props {
    inner: WaveHeroInnerProps | GraduateStoriesInnerProps;
    video: VideoProps;
    backgroundColour?: string;
    textColour?: string;
    showWave?: boolean;
}

const Video: React.FC<Props> = props => {
    const { inner, video, backgroundColour, textColour, showWave = true } = props;

    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const videoRef = useRef() as MutableRefObject<HTMLIFrameElement>;
    const containerRef = useRef() as MutableRefObject<HTMLDivElement>;
    const videoContainerRef = useRef() as MutableRefObject<HTMLDivElement>;
    const imageWrapperRef = useRef() as MutableRefObject<HTMLDivElement>;

    const { breakpoints } = useTheme();
    const isMobile = !useBreakpoint(breakpoints.medium.value);

    const youtubeAPICommand = (func: string) => {
        videoRef.current.contentWindow &&
            videoRef.current.contentWindow.postMessage(
                JSON.stringify({
                    event: 'command',
                    func: func
                }),
                '*'
            );
    };

    // Inner text fade spring
    const innerTextSpring = useSpring({
        paddingTop: isVideoPlaying ? '2rem' : '0rem',
        opacity: isVideoPlaying ? 0 : 1,
        config: { duration: 350 }
    });

    // Video slide in spring
    const videoSpring = useSpring({
        transform: isVideoPlaying ? 'translateX(50%)' : 'translateX(0%)',
        right: isVideoPlaying ? '50%' : '0%',
        config: { duration: 500 },
        onStart: () => {
            !isVideoPlaying && youtubeAPICommand('stopVideo');
            isVideoPlaying && (imageWrapperRef.current.style.display = 'none');
            isVideoPlaying
                ? (containerRef.current.style.minHeight = `${videoContainerRef.current.clientHeight}px`)
                : (containerRef.current.style.minHeight = '');
            videoRef.current.tabIndex = isVideoPlaying ? 1 : -1;
        },
        onRest: () => {
            isVideoPlaying && youtubeAPICommand('playVideo');
            !isVideoPlaying && (imageWrapperRef.current.style.display = 'block');
        }
    });

    // Transcript slide in spring
    const transcriptSpring = useSpring({
        position: 'relative' as const,
        top: isVideoPlaying ? '0rem' : '4rem',
        opacity: isVideoPlaying ? 1 : 0,
        delay: isVideoPlaying ? 350 : 0,
        config: { duration: 350 }
    });

    // Common video JSX between desktop & mobile
    const videoJSX = (
        <VideoInner ref={videoContainerRef}>
            <CloseButton
                aria-label={`Close video ${video.title}`}
                onClick={() => setIsVideoPlaying(false)}
            >
                <CloseIcon />
            </CloseButton>
            <ResponsiveVideoWrapper>
                <Iframe
                    ref={videoRef}
                    src={video.video.src + '?enablejsapi=1'}
                    title={`YouTube video player - ${video.title}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    tabIndex={-1}
                />
            </ResponsiveVideoWrapper>
            {isVideoPlaying &&
                video.transcript &&
                video.transcriptUrl &&
                video.transcript.tableRows.length >= 1 && (
                    <animated.div style={transcriptSpring} data-id="transcript">
                        <H5 as="h2" spacing={{ base: { margin: '1rem 0 1.5rem' } }}>
                            {video.title}
                        </H5>
                        <VideoDescription>
                            <StyledTranscriptLink
                                href={video.transcriptUrl}
                                ariaLabel={`Open transcript video page for ${video.title}`}
                                openInNewTab
                            >
                                Video transcript, with a description of visuals available.
                            </StyledTranscriptLink>
                        </VideoDescription>
                    </animated.div>
                )}
        </VideoInner>
    );

    return (
        <Container
            ref={containerRef}
            $isVideoPlaying={isVideoPlaying}
            style={
                {
                    '--hero-bg-colour': backgroundColour,
                    '--hero-txt-colour': textColour
                } as React.CSSProperties
            }
        >
            {showWave &&
                (!isVideoPlaying || isMobile) &&
                (backgroundColour && backgroundColour === '#FFFFFF' ? <WaveGrey /> : <Wave />)}
            <Grid layout="fill" style={{ flex: 1 }}>
                <Col b={11} m={5}>
                    <animated.div style={!isMobile ? innerTextSpring : undefined}>
                        {'heading' in inner && !('videoCaption' in inner) && <Inner {...inner} />}
                        {'videoCaption' in inner && <GraduateStoriesInner {...inner} />}
                    </animated.div>
                    {isMobile && (
                        <VideoWrapper $isPlaying={isVideoPlaying} style={undefined}>
                            {videoJSX}
                        </VideoWrapper>
                    )}
                </Col>
            </Grid>
            {!isMobile && (
                <VideoWrapper $isPlaying={isVideoPlaying} style={videoSpring}>
                    {videoJSX}
                </VideoWrapper>
            )}
            <ImageWrapper ref={imageWrapperRef}>
                <NextImage
                    src={video.image.src}
                    alt=""
                    fill
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                    priority
                />
                <PlayButton
                    as="button"
                    aria-label={`Play video ${video.title}`}
                    onClick={() => setIsVideoPlaying(true)}
                    className="button c-video-play"
                >
                    <PlayIcon />
                </PlayButton>
            </ImageWrapper>
        </Container>
    );
};

export default Video;
